/**
 * Trade-in activity tabs — history, inbound package, sendback packages,
 * gift cards, escalations, and emails. Each table is a react-admin <Datagrid>
 * (via the shared ActivityGrid) with <TextField>/<DateField>/<FunctionField>
 * columns instead of a hand-built MUI table.
 */
import { DateField, FunctionField, TextField } from 'react-admin';
import { Link } from '@mui/material';

import { ActivityGrid, ActivityTabs, type ActivityTab } from '@src/components/indexPage';

import { formatMoney } from './format';
import { present, type Escalation, type Giftcard, type TradeinEmail, type TradeinRecord, type Ypackage } from './types';

/** A trade-in package row carries a derived label (Inbound / catalog title). */
type LabeledPackage = Ypackage & { _label: React.ReactNode };

const dims = (pkg: Ypackage): string | null =>
  pkg.length && pkg.width && pkg.height ? `${pkg.length}″ × ${pkg.width}″ × ${pkg.height}″` : null;

const tracking = (pkg: Ypackage) => {
  if (!pkg.trackingNumber) return '—';
  return pkg.trackingUrl ?
    <Link href={pkg.trackingUrl} target="_blank" rel="noopener">{pkg.trackingNumber}</Link> :
    pkg.trackingNumber;
};

const PackageGrid = ({ rows, currency }: { rows: LabeledPackage[], currency: string | null | undefined }) => (
  <ActivityGrid rows={rows}>
    <TextField source="_label" label="Package" emptyText="—" />
    <TextField source="state" label="Status" emptyText="—" />
    <DateField source="stateTransitionAt" label="Date" showTime emptyText="—" />
    <FunctionField<Ypackage> label="Carrier" render={pkg => (pkg.carrier ? `${pkg.carrier}${pkg.carrierService ? ` ${pkg.carrierService}` : ''}` : '—')} />
    <FunctionField<Ypackage> label="Dimensions" render={pkg => dims(pkg) ?? '—'} />
    <FunctionField<Ypackage> label="Cost" render={pkg => formatMoney(pkg.actualShippingCost ?? pkg.estimatedShippingCost, currency)} />
    <FunctionField<Ypackage> label="Tracking" render={pkg => tracking(pkg)} />
  </ActivityGrid>
);

export const TradeinActivityTabs = ({ tradein }: { tradein: TradeinRecord }) => {
  const currency = tradein.transactionCurrencyCode;

  // Every list below is filtered through `present` and every tab count is taken from the
  // filtered array, not the raw one. A null element would otherwise reach the datagrid and take
  // down the panel, and a badge counted off the raw list disagrees with the rows beneath it.
  const stateTransitions = present(tradein.stateTransitions);
  const giftcards = present(tradein.giftcards);
  const escalations = present(tradein.escalations);
  const emails = present(tradein.tradeinEmailHistory);

  const inboundRows: LabeledPackage[] = tradein.ypackage ? [{ ...tradein.ypackage, _label: 'Inbound' }] : [];
  const sendbackRows: LabeledPackage[] = present(tradein.sendbackTradeinItems)
    .flatMap(item => (item.sendbackYpackage ?
      [{ ...item.sendbackYpackage, _label: item.catalogItem?.title ?? item.id }] :
      []));

  const tabs: ActivityTab[] = [
    {
      key: 'history',
      label: 'History',
      count: stateTransitions.length,
      render: () => (
        <ActivityGrid rows={stateTransitions}>
          <DateField source="createdAt" label="Timestamp" showTime emptyText="—" />
          <TextField source="changeType" label="Type" emptyText="—" />
          <TextField source="changeFrom" label="From" emptyText="—" />
          <TextField source="changeTo" label="To" emptyText="—" />
          <TextField source="reason" label="Reason" emptyText="—" />
          <TextField source="note" label="Notes" emptyText="—" />
          <TextField source="user.name" label="User" emptyText="—" />
        </ActivityGrid>
      ),
    },
    {
      key: 'inbound',
      label: 'Inbound Package',
      count: tradein.ypackage ? 1 : 0,
      render: () => <PackageGrid rows={inboundRows} currency={currency} />,
    },
    {
      key: 'sendback',
      label: 'Sendback Packages',
      count: sendbackRows.length,
      render: () => <PackageGrid rows={sendbackRows} currency={currency} />,
    },
    {
      key: 'giftcards',
      label: 'Gift Cards',
      count: giftcards.length,
      render: () => (
        <ActivityGrid rows={giftcards}>
          {/* No fallback to `number`: it is unmasked and unaudited on this path, so it is not
              fetched at all. A card with no lastFour shows a dash. */}
          <FunctionField<Giftcard> label="Card" render={card => (card.lastFour ? `···· ${card.lastFour}` : '—')} />
          <FunctionField<Giftcard> label="Amount" render={card => formatMoney(card.amount, currency)} />
          <TextField source="source" label="Source" emptyText="—" />
          <TextField source="state" label="State" emptyText="—" />
        </ActivityGrid>
      ),
    },
    {
      key: 'escalations',
      label: 'Escalations',
      count: escalations.length,
      render: () => (
        <ActivityGrid rows={escalations}>
          <DateField source="createdAt" label="Created" showTime emptyText="—" />
          <FunctionField<Escalation> label="Status" render={e => (e.deletedAt ? 'Deleted' : e.closedAt ? 'Closed' : 'Open')} />
          <TextField source="triggerTypeDisplay" label="Trigger" emptyText="—" />
          <TextField source="shipmentTypeDisplay" label="Shipment Type" emptyText="—" />
          <TextField source="reasonDisplay" label="Reason" emptyText="—" />
          <TextField source="closedReason" label="Closed Reason" emptyText="—" />
        </ActivityGrid>
      ),
    },
    {
      key: 'emails',
      label: 'Emails',
      count: emails.length,
      render: () => (
        <ActivityGrid rows={emails}>
          <DateField source="date" label="Date" showTime emptyText="—" />
          <FunctionField<TradeinEmail> label="Template" render={email => email.displayName ?? email.emailType ?? '—'} />
          <TextField source="toEmail" label="To" emptyText="—" />
          <TextField source="senderName" label="Sender" emptyText="—" />
          <TextField source="state" label="Status" emptyText="—" />
          <FunctionField<TradeinEmail> label="Subject" render={email => (email.txUrl ? <Link href={email.txUrl} target="_blank" rel="noopener">{email.subjectLine}</Link> : email.subjectLine ?? '—')} />
        </ActivityGrid>
      ),
    },
  ];

  return <ActivityTabs tabs={tabs} />;
};

export default TradeinActivityTabs;
